import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form } from '@ok/okd';

import {
  getRecruitPositionDetail,
  getRecruitPositions,
  getRecruitPositionTypes,
} from './api';
import {
  ALL_FILTER_ID,
  FETCH_ALL_PAGE_SIZE,
  FIRST_PAGE,
  MAX_TOTAL_MB,
  MOBILE_BREAKPOINT_PX,
  SCAN_IDLE_MS,
} from './consts';
import { buildFileErrors, inspectFile } from './utils';

// Mobile breakpoint (matches the LESS breakpoint)
const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX}px)`;

// Read the :positionId route param; '' when absent (treated as falsy)
export const useRecruitPositionId = () => useParams().positionId || '';

export const useHeroVideoLoop = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    let timerId = null;
    const handleEnded = () => {
      timerId = window.setTimeout(() => {
        video.currentTime = 0;
        video.play().catch(() => {});
      }, SCAN_IDLE_MS);
    };

    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
      if (timerId) window.clearTimeout(timerId);
    };
  }, []);

  return videoRef;
};

export const useRecruitPositions = (activeTypeId) => {
  const [positionTypes, setPositionTypes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  // Type ids with open positions; null until the first "All" fetch resolves
  const [availableTypeIds, setAvailableTypeIds] = useState(null);

  useEffect(() => {
    getRecruitPositionTypes()
      .then((types) => {
        setPositionTypes(types);
      })
      .catch(() => {
        // Error toast is handled by dataProxy
      });
  }, []);

  // Refetch positions whenever the active filter changes
  useEffect(() => {
    // Ignore a stale response on filter change or unmount
    let cancelled = false;
    setLoading(true);
    getRecruitPositions({
      current: FIRST_PAGE,
      pageSize: FETCH_ALL_PAGE_SIZE,
      positionTypeId: activeTypeId,
    })
      .then((result) => {
        if (cancelled) return;
        const rows = result.rows || [];
        setJobs(rows);
        // Only the All fetch sees every position; record type ids to hide empty tabs
        if (activeTypeId === ALL_FILTER_ID) {
          setAvailableTypeIds(new Set(rows.map((j) => j.positionTypeId)));
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Error toast is handled by dataProxy
        setJobs([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTypeId]);

  const availableTypes = useMemo(
    () =>
      availableTypeIds
        ? positionTypes.filter((t) => availableTypeIds.has(t.id))
        : positionTypes,
    [positionTypes, availableTypeIds]
  );

  return { jobs, loading, availableTypes };
};

// Load position detail, refetch on positionId change, clear on failure
export const usePositionDetail = (positionId) => {
  const [detail, setDetail] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!positionId) return undefined;

    // Ignore a stale response on positionId change or unmount
    let cancelled = false;
    setIsLoading(true);
    getRecruitPositionDetail(positionId)
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
      })
      .catch(() => {
        if (cancelled) return;
        // dataProxy already toasted; just reset state
        setDetail({});
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [positionId]);

  return { detail, isLoading };
};

export const useRecaptchaLoader = () => {
  useEffect(() => {
    const siteKey = window.okGlobal?.recaptchaSiteKey;
    if (!siteKey || document.querySelector('script[data-recaptcha]')) return;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
      siteKey
    )}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptcha = '1';
    script.onerror = () => {
      // eslint-disable-next-line no-console
      console.error('Failed to load reCAPTCHA');
    };
    document.head.appendChild(script);
  }, []);
};

// Track mobile viewport via matchMedia (lazy init guards no-window)
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const handleChange = (event) => setIsMobile(event.matches);
    handleChange(query); // sync once in case size changed before the listener attached
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
};

// True once scrolled, switching the header to its compact "roll" variant; disableOnMobile keeps the full header on mobile
export const useScrollRoll = ({ disableOnMobile = false } = {}) => {
  const isMobile = useIsMobile();
  const [isRoll, setIsRoll] = useState(false);

  useEffect(() => {
    if (disableOnMobile && isMobile) {
      setIsRoll(false);
      return undefined;
    }
    const onScroll = () => {
      // Read scroll position with cross-browser fallbacks
      const scrollY =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop;
      setIsRoll(scrollY > 0);
    };
    onScroll(); // init for an already-scrolled page
    // useCapture=true to catch scrolls on nested scroll containers
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [disableOnMobile, isMobile]);

  return isRoll;
};

export const useApplicationForm = () => {
  // True until the CV upload is first touched (defers the "required" message)
  const [isCvUntouched, setIsCvUntouched] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    birthday: '',
    email: '',
    location: '',
    expectedAnnualSalary: '',
    curriculumVitae: null, // required main resume (CV)
    jobResumes: [null, null], // up to two optional supplementary files
    comment: '',
  });
  const form = Form.useForm();

  const onFormValuesChange = (changedValues, allValues) => {
    setFormData((prev) => ({ ...prev, ...allValues }));
  };

  const setFormDataByKey = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Update one optional resume slot by index (immutable)
  const setFormDataJobResumes = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      jobResumes: prev.jobResumes.map((item, i) =>
        i === index ? value : item
      ),
    }));
  };

  // Per-file validity (format + size); deps are the file fields only (replaced immutably) to skip recompute on text keystrokes
  const cvCheck = useMemo(
    () => inspectFile(formData.curriculumVitae),
    [formData.curriculumVitae]
  );
  const jobResumeChecks = useMemo(
    () => formData.jobResumes.map(inspectFile),
    [formData.jobResumes]
  );

  // Combined size of all uploads must stay within the total cap
  const validSizeMB = (check) =>
    check.isFile && check.formatValid && check.sizeValid ? check.sizeMB : 0;
  const totalSizeMB =
    validSizeMB(cvCheck) +
    jobResumeChecks.reduce((sum, c) => sum + validSizeMB(c), 0);
  const isTotalSizeValid = totalSizeMB <= MAX_TOTAL_MB;

  const curriculumVitaeErrors = buildFileErrors(cvCheck, {
    includeRequired: true,
    requiredSatisfied: cvCheck.isFile || isCvUntouched,
  });
  const jobResumesErrors = jobResumeChecks.map((check) =>
    buildFileErrors(check)
  );

  const passTextFields =
    formData.lastName?.length > 0 &&
    formData.firstName?.length > 0 &&
    Boolean(formData.birthday) &&
    formData.email?.length > 0 &&
    formData.location?.length > 0;
  const canSubmit =
    passTextFields &&
    cvCheck.isFile &&
    curriculumVitaeErrors.length === 0 &&
    jobResumesErrors.every((errs) => errs.length === 0) &&
    isTotalSizeValid;

  return {
    formData,
    onFormValuesChange,
    setFormDataByKey,
    setFormDataJobResumes,
    isCvUntouched,
    setIsCvUntouched,
    submitLoading,
    setSubmitLoading,
    form,
    canSubmit,
    isTotalSizeValid,
    curriculumVitaeErrors,
    jobResumesErrors,
  };
};
